import {AsyncMultiChoice, Chips, Separator, useTranslationContext} from 'platform/components';
import {HStack, VStack} from 'platform/foundation-tw';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {always} from 'ramda';
import {isArray, isNilOrEmpty, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {useHttpCalls} from '../../api/useHttpCalls';
import {useFilterOnChipsChange} from '../../hooks/useFilterOnChipsChange';
import type {EagFilterProps} from '../../types/EagFilterProps';
import {getFilterFieldName} from '../../utils/getFilterFieldName';

const EMPTY_VALUE = decodeURI('%00');
const FILTER_VALUE_KEY = 'value';

export type SuggestionFilterProps = EagFilterProps & {
  suggestId: string;
  quickFilters?: Array<{value: string; label: string}>;
};

export function SuggestionFilter(props: SuggestionFilterProps) {
  const http = useHttpCalls();
  const t = useTranslationContext();
  const isActive = useRef<boolean>(false);

  const model = props.value[FILTER_VALUE_KEY] as string[] | string | null;

  const setSelectedIds = (values: string[] | string | null) => {
    isActive.current = isNotNilOrEmpty(values);
    props.onChange({[FILTER_VALUE_KEY]: values});
  };

  const {onChipsChange} = useFilterOnChipsChange({
    setFilterValue: setSelectedIds,
    defaultValue: [],
  });

  const optionsCacheRef = useRef(new Map<string, Array<{value: string; label: string}>>());
  const MAX_CACHE_SIZE = 20;

  const fetchOptions = async (query: string) => {
    const cacheKey = `${props.suggestId}:${query}`;
    const cached = optionsCacheRef.current.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const getOptionsArgs = {
      suggestId: props.suggestId,
      inputValue: isNilOrEmpty(query) ? EMPTY_VALUE : query,
      limit: 20,
    };
    const result = await http.getSuggestionOptions(getOptionsArgs);
    const options = result ?? [];

    // LRU eviction: remove oldest entry when cache is full
    if (optionsCacheRef.current.size >= MAX_CACHE_SIZE) {
      const firstKey = optionsCacheRef.current.keys().next().value;
      if (firstKey !== undefined) {
        optionsCacheRef.current.delete(firstKey);
      }
    }
    optionsCacheRef.current.set(cacheKey, options);
    return options;
  };

  const fetchLabels = async (values: Array<string>) => {
    const getLabelsArgs = {
      suggestId: props.suggestId,
      keys: values,
    };
    const result = await http.getSuggestionLabels(getLabelsArgs);
    return result ?? [];
  };

  const onChange = (values: string[] | null) => {
    const newSelectedIds = match(values)
      .when(isNotNilOrEmpty, (val) => val)
      .otherwise(always(null));

    setSelectedIds(newSelectedIds);
  };

  const isQuickFilterValue = isString(model) ? [model] : null;

  return (
    <VStack>
      {props.quickFilters && props.quickFilters.length > 0 && (
        <>
          <HStack>
            <Chips
              data-testid="quick-filters-suggestion-filter"
              isDisabled={props.isDisabled}
              value={isQuickFilterValue}
              options={props.quickFilters}
              onChange={onChipsChange}
              isMultiple={false}
              isDeselectable
            />
          </HStack>
          <Separator />
        </>
      )}
      <AsyncMultiChoice<string>
        value={isArray(model) ? model : ([model].filter(Boolean) as string[])}
        onChange={onChange}
        loadOptions={fetchOptions}
        loadLabels={(values) => fetchLabels(values as string[])}
        defaultOptions
        noOptionsMessage={() => t('page.datagrid.labels.noSuggestFilterOptions')}
        placeholder={t('page.datagrid.filter.suggestPlaceholder', {
          field: getFilterFieldName(props.column),
        })}
        isDisabled={props.isDisabled}
        isNotClearable
        menuInPortal
        data-testid={suffixTestId('multi-choice-suggestion-filter', props)}
      />
    </VStack>
  );
}
